import { assetUrl as appAssetUrl, localUrl as appLocalUrl } from '../app-path.js';
import slugify from 'slugify';
import {
	idKeyByType,
	keyByType,
	pathByType,
	toggleFavorite,
} from './autocompletev2.js';
import geolocation from './geolocation';
import geolocationInstructionsModal from './geolocation-instructions-modal';
import {
	SearchedStorage,
	getGenericData,
} from './local-storage-functions';
import LocalStorageHelper from './local-storage-helper';
import FavoriteCityService from './favorite-city-service';

let isSidebarInitialized = false;
let isPremiumCtaTrackingInitialized = false;
const SIDEBAR_TRANSITION_MS = 300;
let overlayCloseTimeout = null;
const sidebar = {};
const sidebarEventIds = {
	open: 'Menu_MainNav',
	close: 'Menu_MainNav',
};

const setupPremiumCtaTracking = () => {
	if (isPremiumCtaTrackingInitialized) return;

	document.addEventListener('click', event => {
		if (!(event.target instanceof Element)) return;

		const premiumCta = event.target.closest('[data-premium-cta]');
		if (!premiumCta?.dataset.id) return;

		pushDatalayer({
			event: 'click_button',
			id: premiumCta.dataset.id,
		});
	});

	isPremiumCtaTrackingInitialized = true;
};

const getSidebarElements = () => ({
	menuToggle: document.getElementById('menuToggle'),
	sidebarEl: document.getElementById('sidebar'),
	overlay: document.getElementById('overlay'),
});

const openSidebar = () => {
	const { menuToggle, sidebarEl, overlay } = getSidebarElements();
	if (!menuToggle || !sidebarEl || !overlay) return false;
	if (sidebarEl.classList.contains('is-open')) return true;

	if (overlayCloseTimeout) {
		clearTimeout(overlayCloseTimeout);
		overlayCloseTimeout = null;
	}

	overlay.classList.remove('hidden');
	requestAnimationFrame(() => {
		overlay.classList.add('is-visible');
	});
	sidebarEl.classList.add('is-open');
	document.body.classList.add('sidebar-is-open');
	menuToggle?.setAttribute('aria-expanded', 'true');
	pushDatalayer({ event: 'open_element', id: sidebarEventIds.open });
	return true;
};

const closeSidebar = () => {
	const { menuToggle, sidebarEl, overlay } = getSidebarElements();
	if (!menuToggle || !sidebarEl || !overlay) return false;

	const wasOpen = sidebarEl.classList.contains('is-open');
	sidebarEl.classList.remove('is-open');
	overlay.classList.remove('is-visible');
	document.body.classList.remove('sidebar-is-open');
	menuToggle?.setAttribute('aria-expanded', 'false');

	const activePanel = sidebarEl.querySelector('.sidebar-panel.is-active');
	if (activePanel) {
		activePanel.classList.remove('is-active');
	}
	const mainPanel = document.getElementById('panel-main');
	if (mainPanel) {
		mainPanel.classList.add('is-active');
	}

	if (wasOpen) {
		pushDatalayer({ event: 'close_element', id: sidebarEventIds.close });
	}

	if (overlayCloseTimeout) {
		clearTimeout(overlayCloseTimeout);
	}
	overlayCloseTimeout = setTimeout(() => {
		overlay.classList.add('hidden');
	}, SIDEBAR_TRANSITION_MS);

	return wasOpen;
};

function createListItem({ label, href, city }, index) {
	const li = document.createElement('li');
	li.className = 'favorites-list-item';

	const a = document.createElement('a');
	a.href = href;
	a.textContent = label;
	a.id = `Menu_MainNav_Item_FavoriteLink-0${index + 1}`;
	a.dataset.id = `Menu_MainNav_Item_FavoriteLink-0${index + 1}`;

	const favoriteButton = document.createElement('button');
	favoriteButton.type = 'button';
	favoriteButton.className = 'favorite-star-button';
	favoriteButton.title = 'Remover dos favoritos';
	favoriteButton.setAttribute('aria-label', 'Remover dos favoritos');
	favoriteButton.dataset.id = 'Menu_MainNav_Button_Star';

	const star = document.createElement('img');
	star.src = appAssetUrl('/dist/images/v2/svg/star-filled.svg');
	star.alt = '';
	star.className = 'favorite-star-icon';
	star.setAttribute('aria-hidden', 'true');
	favoriteButton.appendChild(star);

	// Clique na estrela: alterna favorito
	const typeByPath = { cidade: 'city', litoral: 'beach', aeroporto: 'airport' };
	const type = typeByPath[String(city?.slug || '').split('/')[0]] || 'city';
	favoriteButton.addEventListener('click', async e => {
		e.preventDefault();
		e.stopPropagation();
		await toggleFavorite(
			city,
			'Menu_MainNav_Button_Star',
			type,
			favoriteButton,
		);
		// Atualiza a lista após o evento global
	});

	li.append(a);
	li.append(favoriteButton);
	return li;
}

function buildCityMeta(place) {
	const name = place.cname;
	const uf = LocalStorageHelper.resolveLocationCode(place);
	const label = `${name} - ${uf}`;
	const slug = slugify(String(name)).replaceAll('-', '').toLowerCase();
	const href = appLocalUrl(`/previsao-do-tempo/cidade/${place.cid}/${slug}-${uf.toLowerCase()}`);
	return { name, uf, label, href };
}

function buildSearchMeta(place, type) {
	const name = place[keyByType[type]];
	const uf = LocalStorageHelper.resolveLocationCode(place);
	const label = `${name} - ${uf}`;
	const slug = slugify(String(name)).replaceAll('-', '').toLowerCase();
	const href = appLocalUrl(`/previsao-do-tempo/${pathByType[type]}/${place[idKeyByType[type]]}/${slug}-${uf.toLowerCase()}`);
	return { label, href };
}

function createFavoriteButton(city, type) {
	const button = document.createElement('button');
	button.type = 'button';
	button.className = 'historyFavButton';
	button.dataset.cid = city.cid;

	const icon = document.createElement('img');
	icon.alt = 'Ícone de favorito';
	button.appendChild(icon);

	const updateVisual = isFavorited => {
		const iconName = isFavorited ? 'star-filled' : 'star-empty';
		const title = isFavorited ? 'Remover dos favoritos' : 'Favoritar cidade';
		icon.src = appAssetUrl(`/dist/images/v2/svg/${iconName}.svg`);
		button.title = title;
		button.setAttribute('aria-label', title);
	};

	updateVisual(FavoriteCityService.isCityFavorited(city));

	button.addEventListener('click', async e => {
		e.preventDefault();
		e.stopPropagation();
		await toggleFavorite(city, 'Menu_MainNav_Button_Star', type, button);
	});

	document.addEventListener('favoriteChanged', event => {
		if (isSameFavoriteCity(event.detail.city, city)) {
			updateVisual(event.detail.isFavorited);
		}
	});
	document.addEventListener('favoriteSync', () => {
		updateVisual(FavoriteCityService.isCityFavorited(city));
	});

	return button;
}

function isSameFavoriteCity(firstCity = {}, secondCity = {}) {
	const firstId = FavoriteCityService.getCityStorageId(firstCity);
	const secondId = FavoriteCityService.getCityStorageId(secondCity);

	return Boolean(firstId && secondId && String(firstId) === String(secondId));
}

function resolveMainNavRecentLinkId(paddedIndex, isFavorited) {
	return `Menu_MainNav_${isFavorited ? 'FavoriteItem_NavLink' : 'Item_RecentLink'}-${paddedIndex}`;
}

sidebar.init = () => {
	setupPremiumCtaTracking();

	if (isSidebarInitialized) {
		return;
	}
	const { menuToggle, sidebarEl, overlay } = getSidebarElements();

	if (!menuToggle || !sidebarEl || !overlay) {
		console.warn('Sidebar elements not found!');
		return;
	}

	menuToggle.addEventListener('click', openSidebar);
	overlay.addEventListener('click', closeSidebar);

	sidebar.search.init();

	sidebar.handlePanelNavigation();
	sidebar.populateFavorites();
	FavoriteCityService.refreshServerFavorites().then(() =>
		sidebar.populateFavorites(),
	);
	sidebar.setupTracking();
	isSidebarInitialized = true;
};

sidebar.populateFavorites = () => {
	const favoriteBody = document.getElementById('favorites-list');
	if (!favoriteBody) return;

	const favorites = FavoriteCityService.getVisibleFavorites();

	if (!favorites.length) {
		favoriteBody.innerHTML =
			'<li class="no-favorites">Você não possui localidades favoritas</li>';
	} else {
		favoriteBody.innerHTML = '';
		let count = 0;
		favorites.forEach(favorite => {
			if (count < 3) {
				const meta = buildCityMeta(favorite.city);
				const li = createListItem(
					{ label: meta.label, href: meta.href, city: favorite.city },
					count,
				);
				favoriteBody.append(li);
				count++;
			}
		});
	}
};

// Re-render quando favoritos mudarem
document.addEventListener('favoriteChanged', () => {
	try {
		sidebar.populateFavorites();
	} catch (e) {}
});
document.addEventListener('favoriteSync', () => {
	try {
		sidebar.populateFavorites();
	} catch (e) {}
});

sidebar.handlePanelNavigation = () => {
	const sidebarEl = document.getElementById('sidebar');

	sidebarEl.addEventListener('click', e => {
		const categoryLink = e.target.closest('.has-submenu');
		if (categoryLink) {
			e.preventDefault();
			const targetPanelId = categoryLink.dataset.targetPanel;
			const targetPanel = document.getElementById(targetPanelId);

			if (targetPanel) {
				sidebarEl
					.querySelector('.sidebar-panel.is-active')
					.classList.remove('is-active');
				targetPanel.classList.add('is-active');
			}
		}

		const backButton = e.target.closest('.back-btn');
		if (backButton) {
			const targetPanelId = backButton.dataset.targetPanel;
			const targetPanel = document.getElementById(targetPanelId);

			if (targetPanel) {
				sidebarEl
					.querySelector('.sidebar-panel.is-active')
					.classList.remove('is-active');
				targetPanel.classList.add('is-active');
			}
		}
	});
};

sidebar.setupTracking = () => {
	const sidebarEl = document.getElementById('sidebar');
	window.dataLayer = window.dataLayer || [];

	sidebarEl.addEventListener('click', e => {
		const backButton = e.target.closest('.back-btn');

		if (backButton) {
			const eventData = {
				event: 'click_button',
				id: backButton.dataset.id,
			};

			pushDatalayer(eventData);
		}
	});
};

sidebar.search = {
	elements: {
		input: document.getElementById('sidebarSearchInput'),
		clearButton: document.getElementById('sidebarSearchInputClear'),
		spinner: document.getElementById('sidebarSearchInputSpinner'),
		panel: document.getElementById('sidebarAutocompletePanel'),
		suggestionsUl: document.getElementById('sidebarAutocompleteSuggestions'),
		header: document.getElementById('sidebarAutocompleteHeader'),
		emptyMessage: document.getElementById('sidebarAutocompleteEmptyHistory'),
		clearButton: document.getElementById(
			'sidebarAutocompleteHeaderClearButton',
		),
		myLocationButton: document.getElementById('sidebarSearchMyLocation'),
	},

	init: function () {
		if (!this.elements.input) return;
		this.latestRequestId = 0;
		this.updateInputActionVisibility(
			false,
			Boolean(this.elements.input.value?.trim()),
		);
		this.setupInputListener();
		this.setupClearButton();
		this.setupMyLocationButton();
		this.setupClearHistoryButton();
		this.setupDismissListeners();
	},

	setupInputListener: function () {
		const debouncedHandler = this.debounce(async () => {
			const query = this.elements.input.value?.trim();
			const hasText = Boolean(query);
			this.updateInputActionVisibility(false, hasText);

			if (!query) {
				this.cancelPendingRequest();
				this.elements.panel.style.display = 'flex';
				this.renderSearchHistory();
				return;
			}

			this.elements.panel.style.display = 'flex';

			if (query.length < 3) {
				this.cancelPendingRequest();
				this.renderSearchHistory();
				return;
			}

			const requestId = this.registerRequest();
			this.updateInputActionVisibility(true, true);

			try {
				const response = await $.ajax({
					url: appLocalUrl('/json/busca-por-nome'),
					type: 'POST',
					dataType: 'json',
					data: { name: query },
				});
				if (!this.isLatestRequest(requestId)) return;
				if ((this.elements.input.value || '').trim() !== query) return;
				this.treatResponse(response);
			} catch (error) {
				if (!this.isLatestRequest(requestId)) return;
				console.error('Erro na busca da sidebar:', error);
			} finally {
				if (this.isLatestRequest(requestId)) {
					this.updateInputActionVisibility(false, true);
				}
			}
		}, 300);

		this.elements.input.addEventListener('input', debouncedHandler);
		this.elements.input.addEventListener('focus', debouncedHandler);
		this.elements.input.addEventListener('click', () => {
			this.updateInputActionVisibility(
				false,
				Boolean(this.elements.input.value?.trim()),
			);
			if (!this.elements.input.value?.trim()) {
				this.elements.panel.style.display = 'flex';
				this.renderSearchHistory();
			}
		});
	},

	setupClearButton: function () {
		if (!this.elements.clearButton) return;

		this.elements.clearButton.addEventListener('click', event => {
			event.preventDefault();
			this.cancelPendingRequest();
			this.elements.input.value = '';
			this.updateInputActionVisibility(false, false);
			this.elements.panel.style.display = 'flex';
			this.renderSearchHistory();
			this.elements.input.focus();
		});
	},

	registerRequest: function () {
		this.latestRequestId = (this.latestRequestId || 0) + 1;
		return this.latestRequestId;
	},

	isLatestRequest: function (requestId) {
		return requestId === this.latestRequestId;
	},

	cancelPendingRequest: function () {
		this.latestRequestId = (this.latestRequestId || 0) + 1;
		this.updateInputActionVisibility(
			false,
			Boolean(this.elements.input.value?.trim()),
		);
	},

	updateInputActionVisibility: function (isLoading, hasText) {
		if (this.elements.clearButton) {
			this.elements.clearButton.classList.toggle('is-visible', hasText);
		}
		if (this.elements.spinner) {
			this.elements.spinner.classList.toggle(
				'is-visible',
				hasText && isLoading,
			);
		}
	},

	setupDismissListeners: function () {
		document.addEventListener('click', event => {
			if (!this.elements.panel || !this.elements.input) return;
			if (this.elements.panel.offsetParent === null) return;
			const target = event.target;
			if (
				this.elements.panel.contains(target) ||
				this.elements.input.contains(target)
			) {
				return;
			}
			this.hidePanel();
		});

		this.elements.input.addEventListener('blur', () => {
			setTimeout(() => {
				const active = document.activeElement;
				if (this.elements.panel?.contains(active)) return;
				this.hidePanel();
			}, 0);
		});
	},

	treatResponse: function (response) {
		const validTypes = ['city', 'beach', 'airport'];
		const suggestions = response
			.map(({ response: { data }, type }) => ({
				places: Array.isArray(data) ? data.slice(0, 5) : [],
				type: type?.toLowerCase(),
			}))
			.filter(({ type }) => validTypes.includes(type));

		this.renderSuggestions(suggestions);
	},

	renderSuggestions: function (suggestions) {
		const ul = this.elements.suggestionsUl;
		ul.innerHTML = '';
		ul.style.display = 'block';
		this.elements.header.style.display = 'none';
		this.elements.emptyMessage.style.display = 'none';
		if (this.elements.clearButton) {
			this.elements.clearButton.style.display = 'none';
		}

		const titleByType = {
			city: 'Cidades',
			beach: 'Praias',
			airport: 'Aeroportos',
		};

		let globalIndex = 1;

		suggestions.forEach(({ places, type }) => {
			if (!places.length) return;

			const title = document.createElement('p');
			title.textContent = titleByType[type];
			ul.appendChild(title);

			places.forEach(place => {
				const meta = buildSearchMeta(place, type);
				const cityData = LocalStorageHelper.mapCityData(place);
				const favButton = createFavoriteButton(cityData, type);

				const li = document.createElement('li');
				li.className = 'autocomplete-item';

				const paddedIndex = String(globalIndex).padStart(2, '0');
				const isFavorited = FavoriteCityService.isCityFavorited(cityData);
				const aId = `Menu_MainNav_${isFavorited ? 'FavoriteItem' : 'Item'}_NavLink-${paddedIndex}`;

				const a = document.createElement('a');
				a.href = meta.href;
				a.id = aId;
				a.dataset.id = aId;
				a.addEventListener('click', () => {
					const genericData = getGenericData('search');
					const data = {
						...genericData,
						[keyByType[type]]: cityData,
					};
					SearchedStorage.append(data, 10, 'cid', keyByType[type]);
				});

				const span = document.createElement('span');
				span.textContent = meta.label;
				a.appendChild(span);

				const contentWrapper = document.createElement('div');
				contentWrapper.appendChild(a);

				const wrapper = document.createElement('div');
				wrapper.className = 'autocomplete-content';
				wrapper.appendChild(contentWrapper);
				wrapper.appendChild(favButton);

				li.appendChild(wrapper);

				ul.appendChild(li);
				globalIndex++;
			});
		});
	},

	setupClearHistoryButton: function () {
		if (!this.elements.clearButton) return;
		this.elements.clearButton.addEventListener('click', () => {
			SearchedStorage.clear();
			this.renderSearchHistory();
			const trackId =
				this.elements.clearButton.dataset?.id ||
				'Menu_MainNav_Button_DeleteHistory';
			pushDatalayer({ event: 'click_button', id: trackId });
			this.elements.input?.focus();
		});
	},

	setupMyLocationButton: function () {
		if (!this.elements.myLocationButton) return;

		this.elements.myLocationButton.addEventListener('click', async () => {
			const button = this.elements.myLocationButton;
			const span = button.querySelector('span');
			const originalText = span ? span.textContent : '';
			const resetButton = () => {
				if (span) span.textContent = originalText;
				button.disabled = false;
			};
			const shouldOpenInstructions = async () => {
				if (!('geolocation' in navigator)) return true;
				if (!('permissions' in navigator)) return false;
				try {
					const status = await navigator.permissions.query({
						name: 'geolocation',
					});
					return status?.state === 'denied';
				} catch (error) {
					return false;
				}
			};
			const openInstructions = () => {
				closeSidebar();
				geolocationInstructionsModal.open();
				resetButton();
			};

			let permissionState = null;
			try {
				const status = await navigator.permissions.query({
					name: 'geolocation',
				});
				permissionState = status?.state || null;
			} catch (e) {}

			const eventId =
				permissionState === 'granted'
					? 'Menu_MainNav_Button_Location'
					: 'Menu_MainNav_Button_UseLocation';

			button.dataset.id = eventId;

			const eventData = {
				event: 'click_button',
				id: eventId,
			};
			pushDatalayer(eventData);

			if (await shouldOpenInstructions()) {
				openInstructions();
				return;
			}

			if (span) span.textContent = 'Buscando...';
			button.disabled = true;

			try {
				const location = await geolocation.userGeolocation(true, {
					method: eventId,
				});

				if (!location || !location.idcity || !location.city || !location.uf) {
					if (await shouldOpenInstructions()) {
						openInstructions();
						return;
					}
					throw new Error('Dados de localização indisponíveis.');
				}

				const slug = slugify(String(location.city))
					.replaceAll('-', '')
					.toLowerCase();
				const href = appLocalUrl(`/previsao-do-tempo/cidade/${location.idcity}/${slug}-${String(location.uf).toLowerCase()}`);

				window.location.href = href;
			} catch (error) {
				console.error('Erro ao processar localização na sidebar:', error);
				if (await shouldOpenInstructions()) {
					openInstructions();
					return;
				}
				if (span) span.textContent = 'Erro ao buscar';
				setTimeout(() => {
					resetButton();
				}, 3000);
			}
		});
	},

	renderSearchHistory: function () {
		const ul = this.elements.suggestionsUl;
		const header = this.elements.header;
		const emptyMessage = this.elements.emptyMessage;
		const clearButton = this.elements.clearButton;
		const history = SearchedStorage.get();

		ul.innerHTML = '';
		ul.style.display = 'block';

		if (!Array.isArray(history) || history.length === 0) {
			header.style.display = 'none';
			emptyMessage.style.display = 'flex';
			if (clearButton) clearButton.style.display = 'none';
			return;
		}

		header.style.display = 'flex';
		emptyMessage.style.display = 'none';
		if (clearButton) clearButton.style.display = 'inline';

		let index = 0;
		history
			.slice()
			.reverse()
			.forEach(raw => {
				const type = Object.keys(keyByType).find(
					itemType => raw?.[keyByType[itemType]],
				);
				if (!type) return;

				index++;
				const cityData = raw[keyByType[type]];
				const place = LocalStorageHelper.unmapCityData(cityData, type);
				const meta = buildSearchMeta(place, type);
				const favButton = createFavoriteButton(cityData, type);

				const li = document.createElement('li');
				li.className = 'autocomplete-item';

				const paddedIndex = String(index).padStart(2, '0');
				const isFavorited = FavoriteCityService.isCityFavorited(cityData);

				const a = document.createElement('a');
				a.href = meta.href;
				const updateTrackingId = favorited => {
					const nextId = resolveMainNavRecentLinkId(paddedIndex, favorited);
					a.id = nextId;
					a.dataset.id = nextId;
				};
				updateTrackingId(isFavorited);

				const span = document.createElement('span');
				span.textContent = meta.label;
				a.appendChild(span);

				const contentWrapper = document.createElement('div');
				contentWrapper.appendChild(a);

				const wrapper = document.createElement('div');
				wrapper.className = 'autocomplete-content';
				wrapper.appendChild(contentWrapper);
				wrapper.appendChild(favButton);

				li.appendChild(wrapper);
				ul.appendChild(li);

				document.addEventListener('favoriteChanged', event => {
					const updatedCity = event?.detail?.city || {};
					const updatedId = updatedCity.cloc || updatedCity.cid;
					const cityId = cityData.cloc || cityData.cid;
					if (String(updatedId) !== String(cityId)) return;
					updateTrackingId(event.detail.isFavorited);
				});
			});
	},

	hidePanel: function () {
		if (this.elements.panel) {
			this.elements.panel.style.display = 'none';
		}

		const keyByType = { city: 'city', beach: 'beach', airport: 'airport' };
		const pathByType = {
			city: 'cidade',
			beach: 'litoral',
			airport: 'aeroporto',
		};
		let index = 0;

		history
			.slice()
			.reverse()
			.forEach(raw => {
				const type = Object.keys(keyByType).find(
					type => raw?.[keyByType[type]],
				);
				if (!type) return;
				const mapped = raw?.[keyByType[type]];
				if (!mapped) return;

				const name = mapped.cname || '';
				const uf = LocalStorageHelper.resolveLocationCode(mapped);
				const label = name && uf ? `${name} - ${uf}` : mapped.label || name;
				let slug = mapped.slug;

				if (!slug && type === 'city' && mapped.cid && name && uf) {
					slug = `${mapped.cid}/${slugify(String(name)).replaceAll('-', '').toLowerCase()}-${uf.toLowerCase()}`;
				}

				if (!slug) return;
				index += 1;

				const paddedIndex = String(index).padStart(2, '0');
				const isFavorited = FavoriteCityService.isCityFavorited(mapped);
				const aId = resolveMainNavRecentLinkId(paddedIndex, isFavorited);
				const href = appLocalUrl(`/previsao-do-tempo/${pathByType[type]}/${slug}`);

				const li = document.createElement('li');
				li.className = 'autocomplete-item';

				li.innerHTML = `
        <div class="autocomplete-content">
          <div>
            <a href="${href}" id="${aId}" data-id="${aId}">
              <span>${label}</span>
            </a>
          </div>
        </div>
      `;

				ul.appendChild(li);
			});
	},

	debounce: function (fn, delay) {
		let timeout;
		return function (...args) {
			clearTimeout(timeout);
			timeout = setTimeout(() => fn.apply(this, args), delay);
		};
	},
};

function pushDatalayer(data) {
	window.dataLayer = window.dataLayer || [];

	window.dataLayer.push(data);
}

function createAvatarInitial(name) {
	const initial = name.charAt(0).toUpperCase();
	const avatarDiv = document.createElement('div');
	avatarDiv.className = 'sidebar-user-avatar-initial';
	avatarDiv.textContent = initial;

	let hash = 0;
	for (let i = 0; i < name.length; i++) {
		hash = name.charCodeAt(i) + ((hash << 5) - hash);
	}
	const color = `hsl(${hash % 360}, 70%, 50%)`;
	avatarDiv.style.backgroundColor = color;

	return avatarDiv;
}

sidebar.init();

export default sidebar;
